import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  useFormState,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import Checkbox from '@ingka/checkbox';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

import styles from './index.module.css';

export type ControlledCheckboxProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  label?: string;
  // Ingka form field props
  caption?: string;
  readOnly?: boolean;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: () => void;
};

export function ControlledCheckbox<T extends FieldValues>(
  props: PropsWithChildren<ControlledCheckboxProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    rules,
    shouldValidate = true,
    readOnly,
    successMessage,
    label,
    onChange,
    forcedError,
    caption,
  } = props;
  // Error handling
  const { errors } = useFormState({ name });
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate}
          >
            <Checkbox
              {...field}
              onChange={(e) => {
                // Use native "react-hook-form" controlled handler
                field.onChange(e);
                // This is to allow custom change handlers
                onChange?.();
              }}
              className={styles.input}
              id={name}
              label={label}
              data-testid={`${name}Checkbox`}
              disabled={readOnly}
              caption={caption}
              checked={!!field.value}
              value={field.value ? 'yes' : 'no'}
            />
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledCheckbox;
